import React, { useState, useEffect } from 'react';
import { Table, Typography, InputNumber, Switch, Badge } from 'antd';
import { DeleteForeverSharp, RestoreSharp, BlockRounded } from "@mui/icons-material";
import { useEnsureTickersLoaded } from "../../../hooks/useEnsureTickersLoaded";
import TickerChip from "../../../components/TickerChip";
import PortfolioChip from "../../../components/PortfolioChip";
import { stringMixin } from "../../../utils/strings";
import {isUUID} from "../../../utils/strings";

const BuilderTable = ({ items, onItemsUpdate, itemStats }) => {
    const [dataSource, setDataSource] = useState(items);
    const stocksAndEtfs = items.filter(item => item.type === 'stock' || item.type === 'etf').map(item => item.id);
    const { tickers, loading } = useEnsureTickersLoaded(stocksAndEtfs || ['spy']);

    useEffect(() => {
        setDataSource(items); // Sync with external changes
    }, [items]);

    const handleLockChange = (id, checked) => {
        updateItem(id, { _locked: checked });
    };

    const handleWeightChange = (value, record) => {
        updateItem(record.id, { wgt: value });
    };

    const handleToggleFlag = (id, isFlagged) => {
        updateItem(id, { _flagForRemoval: !isFlagged });
    };

    const updateItem = (id, changes) => {
        const newData = dataSource.map(item => {
            if (item.id === id) {
                return { ...item, ...changes };
            }
            return item;
        });
        setDataSource(newData);
        onItemsUpdate(newData);
    };

    const handleOnRow = (record) => {
        if (record.id === 'custodial_cash'){
            return { style: { background: '#f0f0f0' } };
        }
        return record._flagForRemoval ? { style: { background: '#ffebeb' } } : {};
    };

    // Function to get sorted items by metric
    const getTopItems = (metricPath) => {
        if (!itemStats) return [];

        return Object.keys(itemStats)
            .map(key => ({ id: key, value: metricPath(itemStats[key]) }))
            .filter(item => item.value !== undefined && item.value !== null)
            .sort((a, b) => b.value - a.value);
    };

    const topBetaItems = getTopItems(item => item?.stats?.benchmarks?.spy?.beta_1_yr);
    const topHvItems = getTopItems(item => item?.stats?.windows?.['3_mo']?.hv);

    const getBadgeColor = (index) => {
        switch (index) {
            case 0:
                return '#11e9be';
            case 1:
                return '#1163e9';
            case 2:
                return '#4b586f';
            default:
                return null;
        }
    };

    const renderMetricCell = (value, rankIndex) => {
        const badgeColor = getBadgeColor(rankIndex);
        const backgroundColor = rankIndex < 3 ? badgeColor : 'transparent';
        const color = rankIndex < 3 ? 'white' : 'black';

        return (
            rankIndex < 3 ? (
                <Badge count={rankIndex + 1} color={badgeColor}>
                    <span style={{ backgroundColor, color, padding: '5px', borderRadius: '4px' }}>
                        {stringMixin.pct(value)}
                    </span>
                </Badge>
            ) : (
                <span style={{ padding: '5px', borderRadius: '1px' }}>
                    {stringMixin.pct(value)}
                </span>
            )
        );
    };

    const columns = [
        {
            title: 'Remove',
            dataIndex: 'remove',
            key: 'remove',
            render: (_, record) =>{
                if (record.id === 'custodial_cash') {
                    return <BlockRounded/>
                }
                return (
                    record._flagForRemoval ? (
                        <RestoreSharp onClick={() => handleToggleFlag(record.id, true)} style={{ cursor: 'pointer' }} />
                    ) : (
                        <DeleteForeverSharp  onClick={() => handleToggleFlag(record.id, false)} style={{ cursor: 'pointer', color: 'var(--red)' }} />
                    )
                )
            },
            width: 40
        },
        {
            title: 'Locked',
            dataIndex: '_locked',
            key: '_locked',
            render: (text, record) => (
                <Switch checked={record._locked} onChange={checked => handleLockChange(record.id, checked)} />
            ),
            width: 40
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => {
                const tickerItem = tickers.find(ticker => ticker.symbol === record.id);
                if (tickerItem) {
                    return <TickerChip ticker={tickerItem} />;
                }
                const isPortfolio = isUUID(record.id);
                return isPortfolio ? <PortfolioChip portfolioId={record.id} portfolioName={record.name} portfolioType={record.type} /> : <Typography.Text>{text}</Typography.Text>;
            },
        },
        {
            title: 'Weight',
            dataIndex: 'wgt',
            key: 'wgt',
            render: (text, record) => (
                <InputNumber min={0} max={100} value={record.wgt} onChange={value => handleWeightChange(value, record)} disabled={record._locked} precision={2} />
            ),
        },
    ];

    if (itemStats) {
        columns.push(...[
            {
                title: "S&P Beta 1 Year",
                key: 'beta1yr',
                dataIndex: 'id',
                render: (text, record) => {
                    const betaValue = itemStats?.[text]?.stats?.benchmarks?.spy?.beta_1_yr;
                    const rankIndex = topBetaItems.findIndex(item => item.id === text);
                    return renderMetricCell(betaValue, rankIndex);
                },
                sorter: (a, b) => {
                    if (a.id === 'custodial_cash') return -1; // Keep custodial cash fixed
                    if (b.id === 'custodial_cash') return 1;  // Keep custodial cash fixed
                    return (itemStats?.[a.id]?.stats?.benchmarks?.spy?.beta_1_yr || 0) - (itemStats?.[b.id]?.stats?.benchmarks?.spy?.beta_1_yr || 0);
                }            },
            {
                title: "3 Month Volatility",
                key: 'hv3mo',
                dataIndex: 'id',
                render: (text, record) => {
                    const hvValue = itemStats?.[text]?.stats?.windows?.['3_mo']?.hv;
                    const rankIndex = topHvItems.findIndex(item => item.id === text);
                    return renderMetricCell(hvValue, rankIndex);
                },
                sorter: (a, b) => {
                    if (a.id === 'custodial_cash') return -1; // Keep custodial cash fixed
                    if (b.id === 'custodial_cash') return 1;  // Keep custodial cash fixed
                    return (itemStats?.[a.id]?.stats?.windows?.['3_mo']?.hv || 0) - (itemStats?.[b.id]?.stats?.windows?.['3_mo']?.hv || 0);
                }
            },

        ]);
    }

    return (
        <Table
            dataSource={dataSource}
            columns={columns}
            onRow={handleOnRow}
            rowKey="id"
            pagination={false}
            style={{
                maxHeight: '85vh',
                overflowY: 'scroll',
                width: '100%'
            }}
        />
    );
};

export default BuilderTable;
